import { Alert, Avatar, Box, Button, ButtonGroup, Checkbox, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Modal, ModalDialog, ModalOverflow, Stack, Tooltip, Typography } from "@mui/joy";
import { getFileExtension, getFileExtensionIcon, isImage } from "@utils/files";
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import RotateLeftRoundedIcon from '@mui/icons-material/RotateLeftRounded';
import RotateRightRoundedIcon from '@mui/icons-material/RotateRightRounded';
import toast from "react-hot-toast";
import { useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { FieldEditTable } from "./FieldEditTable";
import WarningDialog from './dialogs/WarningDialog';
import { authenticatedApi } from "api";
import { randomString } from "@utils/randomString";
import { events } from "@utils/analytics";

const ensureSaneRotation = (newRotation) => {
    if(newRotation > 360) {
        newRotation -= 360;
    } else if(newRotation < 0) {
        newRotation += 360;
    }
    return newRotation;
};

const nameClickToView = (e, file) => {
    e.preventDefault();

    if(!file.url) {
        toast.error('This file is invalid. Please contact Maestro support.');
        // alert('This file is invalid. Please contact maestro support.');
        return;
    }
    window.open(file.url, '_blank');//.focus();
}

const fields = [
    {
        name: 'name',
        type: 'info',
        content: (file) => (<>{file.name} (<a href="#" onClick={e => nameClickToView(e, file)}>Click here</a> to view)</>)
    },
    {
        name: 'caption',
        type: 'text',
        placeholder: 'e.g. A group of people enjoying music in the park',
    },
    {
        name: 'visibility',
        type: 'select',
        options: [
            {label: 'Public', value: 1},
            {label: 'Private', value: 0},
        ]
    },
    {
        name: 'rotate',
        type: 'info',
        conditional: object => isImage(object),
        content: (file, setValue) => (<>
            <ButtonGroup spacing={1.5}>
            <Tooltip title="Rotate Counter-clockwise">
            <IconButton
                variant="outlined"
                size="sm"
                sx={{
                    bgcolor: 'background.surface',
                }}
                onClick={() => setValue('rotation', ensureSaneRotation(file.rotation - 90))}
            >
                <RotateLeftRoundedIcon/>
            </IconButton>
            </Tooltip>
            <Tooltip title="Rotate Clockwise">
            <IconButton
                variant="outlined"
                size="sm"
                sx={{
                    bgcolor: 'background.surface',
                }}
                onClick={() => setValue('rotation', ensureSaneRotation(file.rotation + 90))}
            >
                <RotateRightRoundedIcon/>
            </IconButton>
            </Tooltip>
            </ButtonGroup>
        </>)
    },
];

function EditFile({ layout, file, onChange }) {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    /*
    const [rotation, _unstable_setRotation] = useState(0);
    const setRotation = (newRotation:number) => {
        if(newRotation > 360) {
            newRotation -= 360;
        } else if(newRotation < 0) {
            newRotation += 360;
        }
        _unstable_setRotation(newRotation);
    };
    const rotate = (direction:string) => {
        setRotation(direction === 'CCW' ? rotation - 90 : rotation + 90);
    }
    */

    const _isImage = isImage(file);

    let defaultValues = {
        ...file,
        rotation: 0,
    };

    const formMethods = useForm({
        defaultValues,
    });
    const {
        setValue,
        reset,
        handleSubmit,
        watch,
        getValues,
        formState: { isSubmitting, errors, isValid, isDirty }
    } = formMethods;

    useEffect(() => {
        reset(defaultValues);
    }, [file]);

    const rotation = getValues('rotation');

    const onDelete = () => {
        const performDelete = authenticatedApi.delete(`/files/${file.id}`);

        toast.promise(performDelete, {
            loading: `Deleting...`,
            success: `The file was deleted.`,
            error: (err) => `There was an error while deleting the file.`,
        });

        performDelete.then(() => {
            onChange(file, 'deleted');
            setOpen(false);
            events.fileDeleted();
        });
    };

    const onSubmit = (fields) => {
        const performUpdate = authenticatedApi.put(`/files/${file.id}`, fields);

        toast.promise(performUpdate, {
            loading: `Saving...`,
            success: `The file was saved.`,
            error: (err) => `There was an error while saving the file.`,
        });

        performUpdate.then(response => {
            const modifiedImage = response.data;
            // add CB or update if it already exists (this happens when the user hits the save button on the object)
            let cacheBuster = randomString();

            if(modifiedImage.url) {
                if(modifiedImage.url.indexOf('?cb=') !== -1) {
                    modifiedImage.url = modifiedImage.url.split('?cb=')[0];
                }
                modifiedImage.url = `${modifiedImage.url}?cb=${cacheBuster}`;
            }

            if(modifiedImage.thumbnail_url) {
                if(modifiedImage.thumbnail_url.indexOf('?cb=') !== -1) {
                    modifiedImage.thumbnail_url = modifiedImage.thumbnail_url.split('?cb=')[0];
                }
                modifiedImage.thumbnail_url = `${modifiedImage.thumbnail_url}?cb=${cacheBuster}`;
            }

            onChange(modifiedImage);
            setOpen(false);
        });
    };

    return (<>
        <Tooltip title="Edit File">
        <IconButton
            size={layout === 'grid' ? 'md' : 'sm'}
            // color="neutral"
            variant="outlined"
            sx={{
                bgcolor: 'background.surface',
            }}
            onClick={(e) => {
                e.preventDefault();
                setOpen(true);
            }}
            >
            <EditRoundedIcon />
        </IconButton>
        </Tooltip>
        <Modal open={open} onClose={() => saving ? null : setOpen(false)}>
            <ModalOverflow>
            <form onSubmit={handleSubmit(onSubmit)}>
            <FormProvider {...formMethods}>
                <ModalDialog sx={{width: '100%', maxWidth: '800px'}}>
                    <DialogTitle
                        sx={{
                            display: 'flex',
                            'span': {
                                flex: 1,
                            }
                        }}
                    >
                        <span>Edit File</span>
                        <WarningDialog
                            title="Confirm Delete"
                            content={<>Are you sure you want to delete this?</>}
                            confirmText="Delete"
                            onConfirm={onDelete}
                            activateButton={(btnProps)=>
                                <Button
                                    disabled={saving}
                                    // variant="plain"
                                    // color="neutral"
                                    size="sm"
                                    sx={{
                                        borderRadius: '3.5px' // todo: fix
                                    }}
                                    {...btnProps}
                                >
                                    Delete
                                </Button>
                            }
                        />
                    </DialogTitle>
                    <Divider/>
                    <Box sx={{
                        marginInline: 'var(--Divider-inset)',
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 2
                    }}
                    >
                        <Box sx={{flex: '1 1 66%'}}>
                        <FieldEditTable mode="row" fields={fields}/>
                        </Box>
                        {_isImage === true && (
                        <Box sx={{
                            flex: '1 1 34%',
                            /*borderLeft: 1, borderLeftColor: 'var(--joy-palette-divider)'*/
                        }}>
                            <img src={file?.thumbnail_url} style={{transform: `rotate(${rotation}deg)`, width: '100%'}}/>
                        </Box>
                        )}
                    </Box>
                    <DialogActions>
                        <Button
                            disabled={!isValid}
                            loading={saving}
                            type="button" // this can't be submit type or it will submit the parent form
                            onClick={() => {
                            onSubmit(getValues());
                            }}
                        >
                            Save
                        </Button>
                        <Button
                            disabled={saving}
                            variant="plain"
                            color="neutral"
                            onClick={() => {
                                setOpen(false);
                                try {
                                    // onCancel();
                                } catch(e) {
                                    //
                                }
                            }}
                        >
                            Cancel
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </FormProvider>
            </form>
            </ModalOverflow>
        </Modal>
    </>)
}

export function FileThumbnail({
    file,
    layout = 'list',
    editable = false,
    fileDidChange = () => {},
}) {
    if(!file) {
        return null;
    }

    const _isImage = isImage(file);
    const fileExtension = getFileExtension(file);
    const fileIcon = getFileExtensionIcon(file);

    return (
        <Tooltip title={layout === 'grid' && file.name}>
            <Stack
                direction={layout === 'list' ? 'row' : 'column'}
                justifyContent="flex-start"
                alignItems="center"
                spacing={2}
                sx={{
                    aspectRatio: layout === 'list' ? 'unset' : 1,
                    position: 'relative',
                }}
                /*
                onClick={() => {
                    if(!file.url) {
                        toast.error('This file is invalid. Please contact Maestro support.');
                        // alert('This file is invalid. Please contact maestro support.');
                        return;
                    }
                    window.open(file.url, '_blank');//.focus();
                    // file.url;
                    // console.log('foobar');
                }}
                */
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: 1,
                        gap: 2,
                        cursor: 'pointer',
                        '&:hover': {
                            '.normal-icon': {
                                // opacity: 0,
                            },
                            '.hover-icon': {
                                opacity: 1,
                            },
                            //
                        },
                    }}
                >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',

                    }}
                >
                    <Avatar
                        src={file.thumbnail_url}
                        size="lg"
                        variant="solid"
                        sx={{
                            '--Avatar-radius': 'var(--joy-radius-sm)',
                            ...((layout === 'grid') && {
                                '--Icon-fontSize': '3.5vw',
                                width: '100%',
                                height: 'unset',
                                aspectRatio: 1,
                                position: 'absolute',
                                top: 0,
                                'svg': {
                                    // 8px comes from the py: 0.5
                                    transform: 'translateY(calc((var(--joy-fontSize-sm) + 8px) * -0.5))',
                                }
                            })
                        }}
                    >
                        {/* <Box sx={{position: 'relative'}}> */}
                            <Box className="normal-icon" sx={{position: 'absolute'}}>{fileIcon}</Box>
                        {/* </Box> */}
                    </Avatar>
                    <Avatar
                        className="hover-icon"
                        size="lg"
                        variant="solid"
                        sx={{
                            '--Avatar-radius': 'var(--joy-radius-sm)',
                            position: 'absolute',
                            opacity: 0,
                            transition: '0.2s ease',
                            ...((layout === 'grid') && {
                                '--Icon-fontSize': '3.5vw',
                                width: '100%',
                                height: 'unset',
                                aspectRatio: 1,
                                top: 0,
                                'svg': {
                                    // 8px comes from the py: 0.5
                                    transform: 'translateY(calc((var(--joy-fontSize-sm) + 8px) * -0.5))',
                                }
                            })
                        }}
                        onClick={() => {
                            if(!file.url) {
                                toast.error('This file is invalid. Please contact Maestro support.');
                                // alert('This file is invalid. Please contact maestro support.');
                                return;
                            }
                            window.open(file.url, '_blank');//.focus();
                            // file.url;
                            // console.log('foobar');
                        }}
                    >
                        <LaunchRoundedIcon/>
                        {/* <Box sx={{position: 'relative'}}> */}
                            {/* <Box className="normal-icon" sx={{position: 'absolute'}}>{fileIcon}</Box> */}
                        {/* </Box> */}
                    </Avatar>
                    {layout === 'grid' && editable === true && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                zIndex: 900
                            }}
                        >
                            <EditFile layout={layout} file={file} onChange={fileDidChange} />
                        </Box>
                    )}
                    {/*
                    <Box
                        className="hover-icon"
                        sx={{
                            position: 'absolute',
                            opacity: 1,
                            // color: 'var(--variant-solidColor, var(--joy-palette-neutral-solidColor, var(--joy-palette-common-white, #FFF)))', // copied from avatar
                        }}
                    >
                        <LaunchRoundedIcon/>
                    </Box>
                    */}
                </Box>
                <Typography
                    fontSize={'sm'}
                    sx={theme => ({
                        flex: 1,
                        ...((layout === 'grid') && {
                            position: 'absolute',
                            bottom: 0,
                            mt: 0,
                            px: 1.5,
                            py: 0.5,
                            width: '100%',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textAlign: 'center',
                            textOverflow: 'ellipsis',
                            backgroundColor: 'rgba(0,0,0,0.6)',// theme.palette.background.backdrop,
                            color: theme.palette.common.white,
                            borderBottomLeftRadius: 'var(--joy-radius-sm)',
                            borderBottomRightRadius: 'var(--joy-radius-sm)',
                        })
                    })}
                >
                    {file.name.length ? file.name : 'N/A'}
                </Typography>
                </Box>
                {layout === 'list' && editable === true && (<EditFile layout={layout} file={file} onChange={fileDidChange} />)}
            </Stack>
        </Tooltip>
    );
}
